import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/ui/base/button';
import { Badge } from '@/ui/base/badge';
import { cn } from '@/nucleo/utilitarios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import type { DesignSystemSection } from './sectionRegistry';

interface SectionNavProps {
  sections: DesignSystemSection[];
  value: string;
  onValueChange: (id: string) => void;
  counts?: Record<string, number | undefined>;
  showCounts?: boolean;
}

/**
 * Navegação das seções do Design System.
 * Mobile: seletor. Desktop: faixa rolável com setas e indicadores de corte.
 */
export function SectionNav({
  sections,
  value,
  onValueChange,
  counts,
  showCounts,
}: SectionNavProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateArrows, sections.length]);

  // Mantém a aba ativa visível
  useEffect(() => {
    const el = scrollRef.current;
    const active = el?.querySelector<HTMLElement>(`[data-section-id="${value}"]`);
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [value]);

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const activeSection = sections.find((s) => s.id === value);

  return (
    <div className="space-y-2">
      {/* Mobile: seletor */}
      <div className="md:hidden">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger aria-label="Selecionar seção do Design System">
            <SelectValue placeholder="Seção">
              {activeSection && (
                <span className="flex items-center gap-2">
                  <activeSection.icon className="h-4 w-4" />
                  {activeSection.label}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {sections.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <s.icon className="h-4 w-4" />
                  {s.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: faixa rolável com setas */}
      <div className="hidden md:flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => scrollBy(-1)}
          disabled={!canLeft}
          aria-label="Rolar seções para a esquerda"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="relative flex-1 min-w-0">
          {canLeft && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-background to-transparent z-10" />
          )}
          {canRight && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-background to-transparent z-10" />
          )}
          <div
            ref={scrollRef}
            onScroll={updateArrows}
            role="tablist"
            aria-label="Seções do Design System"
            className="flex gap-1 overflow-x-auto scrollbar-none scroll-smooth rounded-lg bg-muted p-1"
          >
            {sections.map((s) => {
              const active = s.id === value;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-section-id={s.id}
                  onClick={() => onValueChange(s.id)}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    active
                      ? 'bg-background text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <s.icon className="h-3.5 w-3.5" />
                  <span>{s.label}</span>
                  {showCounts && counts?.[s.id] ? (
                    <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                      {counts[s.id]}
                    </Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => scrollBy(1)}
          disabled={!canRight}
          aria-label="Rolar seções para a direita"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
